import { useRuntimeConfig } from "#imports";
import { useConsentStore } from "~~/stores/consentStore";
import { useAuthStore } from "~~/stores/authStore";
import {
  isSalesmanagoEnabled,
  normalizeSalesmanagoHost,
  readSalesmanagoContactId,
  salesmanagoCookieDomains,
  validSalesmanagoContactId,
} from "~~/shared/utils/salesmanago";

/**
 * SALESmanago — kod monitorujący (sm.js) odtworzony dla frontu headless (Nuxt).
 *
 * W klasyku moduł `salesmanago` wstrzykiwał go hookiem displayHeader
 * (`ModuleHelper::generateMonitCode`), którego Nuxt nie renderuje. Tu składamy ten
 * sam snippet: zmienne globalne `_smid` / `_smclt`, stub kolejki `window.sm`, a potem
 * `<script async src="https://<endpoint>/static/sm.js">`. Nawigację SPA sm.js obsługuje
 * sam (podmienia history.pushState + popstate), więc ładujemy go raz na dokument.
 *
 * Tożsamość kontaktu: sm.js przypisuje wizyty do kontaktu po ciasteczku `smclient`.
 * W klasyku ustawiał je backend w odpowiedzi na żądanie, w którym zadziałał hook
 * modułu (logowanie, rejestracja, zamówienie) — i przy KAŻDYM logowaniu nadpisywał je
 * kontaktem bieżącego klienta. W headless te hooki biegną w kolejce workera
 * contelizerrest, więc ID kontaktu odbieramy osobno z
 * `GET /api/store/salesmanago/identity`:
 *  - przed załadowaniem sm.js podajemy je jako `_smclientid` (sm.js daje mu
 *    pierwszeństwo przed ciasteczkiem i sam nadpisuje `smclient`),
 *  - po załadowaniu zapisujemy `smclient` przez `window.smCreateCookie`.
 * UWAGA: sm.js trzyma tożsamość w pamięci do pełnego przeładowania strony — zmiana
 * lub skasowanie `smclient` po załadowaniu działa dopiero od następnego wejścia.
 *
 * Zmiana konta w tej samej przeglądarce: tożsamość ustawioną przez nas zapamiętujemy
 * w localStorage jako `{ userId, contactId }`. Ciasteczko uznajemy za aktualne tylko,
 * gdy rekord należy do bieżącego klienta i zgadza się z `smclient`; inaczej pytamy
 * backend. Gdy rekord należy do innego klienta, a bieżący nie ma kontaktu, kasujemy
 * cudze `smclient`, żeby jego wizyty nie szły na poprzedniego klienta. `smclient` bez
 * rekordu (np. z linku w mailu SM) zostawiamy — nie wiemy, czyje jest.
 *
 * Gate: zgoda marketingowa (jak edrone / Meta Pixel), rozstrzygnięta tożsamość
 * (`authStore.isResolved`) i ten sam warunek „włączone” co CSP i baner zgody
 * (`isSalesmanagoEnabled`). Cofnięcie zgody kasuje ciasteczka SM i — jeśli sm.js już
 * działa — przeładowuje stronę, bo sm.js śledziłby dalej nawigację SPA.
 */

/** `window.sm`: przed załadowaniem sm.js — stub kolejki, potem obiekt sm.js. */
interface SalesmanagoFn {
  (...args: unknown[]): void;
  q?: unknown[][];
}

declare global {
  interface Window {
    _smid?: string;
    _smclt?: string;
    _smclientid?: string;
    SalesmanagoObject?: string;
    sm?: SalesmanagoFn;
    /**
     * Zapis ciasteczka przez sm.js (`save` z dowiązanym `this`). NIE używać
     * `sm.api.createCookie`: jest niedowiązane, `save` czyta wtedy
     * `this.defaultLifetime` jako undefined i zapisuje ciasteczko już wygasłe.
     */
    smCreateCookie?: (name: string, value: string) => void;
  }
}

const IDENTITY_ENDPOINT = "/api/store/salesmanago/identity";
const CONTACT_COOKIE = "smclient";
// Ciasteczka SM kasowane po cofnięciu zgody: co najmniej tożsamość kontaktu
// (`smclient`) i identyfikator urządzenia (`smuuid`) oraz zdarzenia ustawiane przez
// moduł PS. Lista do uzupełnienia, jeśli konto SM zakłada kolejne.
const SM_COOKIES = ["smclient", "smuuid", "smevent", "smclient_event"] as const;
// Ciasteczka kasowane po usunięciu konta: kontakt i powiązany z nim w SM
// identyfikator urządzenia — tak samo, jak robi to sm.js przy czyszczeniu kontaktu.
const CONTACT_IDENTITY_COOKIES = [CONTACT_COOKIE, "smuuid"] as const;
// Najdłużej tyle czekamy na ID kontaktu przed załadowaniem sm.js — monitoring nie
// może wisieć na wolnym backendzie, a bez ID sm.js i tak działa (anonimowo).
const IDENTITY_TIMEOUT_MS = 1500;
// Worker kolejki SM biegnie co ~2 min. Drugie pytanie po tym czasie łapie kontakt
// założony przez hook zamówienia / logowania, który właśnie czeka w kolejce.
const IDENTITY_FOLLOW_UP_MS = 150_000;
// Memo „pytaliśmy, kontaktu brak” per użytkownik — pełne przeładowania strony nie
// pytają backendu za każdym razem. Po TTL pytamy ponownie, bo worker ponawia
// nieudane zadania z backoffem (1 / 5 / 15 min).
const MISS_MEMO_KEY = "sm-identity-miss";
const MISS_MEMO_TTL_MS = 15 * 60 * 1000;
// Tożsamość ustawiona przez nas: { userId, contactId } (localStorage — przeżywa sesję
// karty, tak jak ciasteczko `smclient`).
const IDENTITY_RECORD_KEY = "sm-identity";

interface IdentityRecord {
  userId: number;
  contactId: string;
}

// Stan współdzielony między wywołaniami composable — sm.js jest jeden na dokument.
let scriptAttached = false;
let bootstrapping = false;
let lastUserId: number | null = null;
let followUpUserId: number | null = null;
let followUpTimer: ReturnType<typeof setTimeout> | null = null;

function readContactCookie(): string | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${CONTACT_COOKIE}=([^;]*)`));
  const raw = match?.[1]?.trim();
  if (!raw) return null;
  try {
    return decodeURIComponent(raw);
  } catch {
    return raw;
  }
}

/** Kasuje ciasteczka w wariancie host-only i na każdej domenie nadrzędnej. */
function deleteCookies(names: readonly string[]): void {
  if (typeof document === "undefined") return;
  const expired = "expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/";
  const domains = salesmanagoCookieDomains(window.location.hostname);
  for (const name of names) {
    document.cookie = `${name}=; ${expired}`;
    for (const domain of domains) document.cookie = `${name}=; ${expired}; domain=${domain}`;
  }
}

function readRecord(): IdentityRecord | null {
  try {
    const raw = localStorage.getItem(IDENTITY_RECORD_KEY);
    if (!raw) return null;
    const record = JSON.parse(raw) as { userId?: unknown; contactId?: unknown };
    const contactId = validSalesmanagoContactId(record.contactId);
    return typeof record.userId === "number" && contactId
      ? { userId: record.userId, contactId }
      : null;
  } catch {
    return null;
  }
}

function saveRecord(record: IdentityRecord): void {
  try {
    localStorage.setItem(IDENTITY_RECORD_KEY, JSON.stringify(record));
  } catch { /* zablokowany storage — najwyżej zapytamy ponownie */ }
}

function forgetRecord(): void {
  try {
    localStorage.removeItem(IDENTITY_RECORD_KEY);
  } catch { /* noop */ }
}

/** `smclient` należy do tego klienta i ustawiliśmy go my — nie ma o co pytać. */
function identityKnown(userId: number): boolean {
  const record = readRecord();
  return !!record && record.userId === userId && readContactCookie() === record.contactId;
}

function isMissRemembered(userId: number): boolean {
  try {
    const raw = sessionStorage.getItem(MISS_MEMO_KEY);
    if (!raw) return false;
    const memo = JSON.parse(raw) as { userId?: unknown; at?: unknown };
    return memo.userId === userId
      && typeof memo.at === "number"
      && Date.now() - memo.at < MISS_MEMO_TTL_MS;
  } catch {
    return false;
  }
}

function rememberMiss(userId: number): void {
  try {
    sessionStorage.setItem(MISS_MEMO_KEY, JSON.stringify({ userId, at: Date.now() }));
  } catch { /* zablokowany storage (tryb prywatny) — najwyżej zapytamy ponownie */ }
}

function forgetMiss(): void {
  try {
    sessionStorage.removeItem(MISS_MEMO_KEY);
  } catch { /* noop */ }
}

function clearFollowUp(): void {
  if (followUpTimer) clearTimeout(followUpTimer);
  followUpTimer = null;
  followUpUserId = null;
}

/**
 * Zapomina tożsamość kontaktu zapisaną w przeglądarce: zaplanowane ponowienie,
 * podane ciasteczka SM, rekord tożsamości i memo „brak kontaktu”.
 */
function clearStoredIdentity(cookies: readonly string[]): void {
  clearFollowUp();
  deleteCookies(cookies);
  forgetRecord();
  forgetMiss();
}

/**
 * ID kontaktu bieżącej sesji. `null` = backend potwierdził brak kontaktu,
 * `undefined` = nie wiadomo (timeout, błąd sieci) — tego nie zapamiętujemy jako braku.
 */
async function fetchContactId(): Promise<string | null | undefined> {
  const controller = typeof AbortController === "undefined" ? null : new AbortController();
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<undefined>((resolve) => {
    timer = setTimeout(() => {
      controller?.abort();
      resolve(undefined);
    }, IDENTITY_TIMEOUT_MS);
  });
  const request = $fetch(IDENTITY_ENDPOINT, {
    credentials: "include",
    signal: controller?.signal,
  })
    .then((payload: unknown) => readSalesmanagoContactId(payload))
    .catch(() => undefined);

  try {
    return await Promise.race([request, timeout]);
  } finally {
    clearTimeout(timer);
  }
}

function appendScript(src: string): void {
  const script = document.createElement("script");
  script.type = "text/javascript";
  script.async = true;
  // sm.js odnajduje własny tag po `src$="/static/sm.js"` (ścieżka chunków),
  // więc atrybut musi kończyć się dokładnie tak — bez query stringa.
  script.src = src;
  document.head.appendChild(script);
}

/** `_smclt` (dni życia `smclient`) — tylko dodatnia liczba całkowita, inaczej domyślny TTL sm.js. */
function readCookieTtlDays(value: unknown): string | null {
  const days = String(value ?? "").trim();
  return /^[1-9]\d*$/.test(days) ? days : null;
}

export function useSalesmanago() {
  const config = useRuntimeConfig().public;
  // Runtime'owe nadpisanie env przechodzi przez destr — „123” potrafi przyjść jako liczba.
  const clientId = String(config.salesmanagoClientId ?? "").trim();
  const host = normalizeSalesmanagoHost(config.salesmanagoEndpoint);
  const cookieTtlDays = readCookieTtlDays(config.salesmanagoCookieTtlDays);
  const popups = String(config.salesmanagoPopups) === "true";
  const enabled = isSalesmanagoEnabled(config);
  const consent = useConsentStore();
  const auth = useAuthStore();

  function marketingAllowed(): boolean {
    return !!consent.current?.marketing;
  }

  /** Zalogowany klient albo gość po zamówieniu — oba mają kontakt w SM. */
  function currentUserId(): number | null {
    const id = Number(auth.currentUser?.id);
    return Number.isInteger(id) && id > 0 ? id : null;
  }

  function attachScripts(): void {
    window._smid = clientId;
    if (cookieTtlDays) window._smclt = cookieTtlDays;
    window.SalesmanagoObject = "sm";
    if (!window.sm) {
      const stub: SalesmanagoFn = (...args: unknown[]) => {
        (stub.q = stub.q || []).push(args);
      };
      window.sm = stub;
    }
    appendScript(`https://${host}/static/sm.js`);
    if (popups) appendScript(`https://${host}/dynamic/${encodeURIComponent(clientId)}/popups.js`);
    scriptAttached = true;
  }

  /** Jedno ponowienie na użytkownika na cykl życia strony — po przebiegu workera. */
  function scheduleFollowUp(userId: number): void {
    if (followUpUserId === userId) return;
    clearFollowUp();
    followUpUserId = userId;
    followUpTimer = setTimeout(() => {
      followUpTimer = null;
      if (currentUserId() !== userId || !marketingAllowed() || identityKnown(userId)) return;
      void syncIdentity(userId);
    }, IDENTITY_FOLLOW_UP_MS);
  }

  /**
   * Pyta backend o kontakt klienta `userId` i ustawia tożsamość dla sm.js.
   *
   * Wyścigi: po `await` sprawdzamy, czy to nadal ten sam klient i czy zgoda trwa —
   * spóźniona odpowiedź dla A (wylogowanie, przelogowanie na B, cofnięcie zgody) jest
   * odrzucana bez żadnych skutków: nie ustawia tożsamości, nie kasuje ciasteczek i nie
   * planuje ponowień.
   *
   * Brak kontaktu (np. zamówienie gościa czeka w kolejce workera — także po powrocie
   * z bramki płatności, czyli na świeżo załadowanej stronie) planuje jedno ponowienie.
   */
  async function syncIdentity(userId: number): Promise<void> {
    const contactId = await fetchContactId();
    if (currentUserId() !== userId || !marketingAllowed()) return;

    if (contactId) {
      forgetMiss();
      saveRecord({ userId, contactId });
      if (readContactCookie() === contactId) return;
      // Przed załadowaniem sm.js wystarczy zmienna globalna (sm.js sam nadpisze
      // `smclient`); po załadowaniu — zapis przez API sm.js. Brak API (sm.js
      // zablokowany) = nic nie robimy, rekord nie zgodzi się z ciasteczkiem i
      // zapytamy przy następnym wejściu.
      if (scriptAttached) window.smCreateCookie?.(CONTACT_COOKIE, contactId);
      else window._smclientid = contactId;
      return;
    }

    if (contactId === null) {
      rememberMiss(userId);
      const record = readRecord();
      if (record && record.userId !== userId) {
        // `smclient` ustawiony przez nas dla poprzedniego klienta — wizyty bieżącego
        // nie mogą iść na jego kontakt.
        deleteCookies([CONTACT_COOKIE]);
        forgetRecord();
      }
    }
    scheduleFollowUp(userId);
  }

  /**
   * Ładuje sm.js (raz na dokument): najpierw — jeśli sesja ma klienta, a jego
   * tożsamość nie jest potwierdzona — pyta o ID kontaktu (najwyżej
   * {@link IDENTITY_TIMEOUT_MS}), potem ustawia zmienne globalne i wstrzykuje skrypt.
   * Idempotentne.
   */
  async function bootstrap(): Promise<void> {
    if (!enabled || typeof window === "undefined") return;
    if (scriptAttached || bootstrapping) return;
    if (!marketingAllowed() || !auth.isResolved) return;

    bootstrapping = true;
    try {
      const userId = currentUserId();
      if (userId && !identityKnown(userId) && !isMissRemembered(userId)) {
        await syncIdentity(userId);
      }
      // Zgoda mogła zostać cofnięta, gdy czekaliśmy na backend.
      if (!marketingAllowed()) return;
      attachScripts();
      lastUserId = userId;
    } finally {
      bootstrapping = false;
    }

    // Użytkownik zmienił się w trakcie pytania o kontakt (np. logowanie tuż po
    // wejściu) — dogoń go tak, jakby zmiana przyszła po załadowaniu sm.js.
    if (currentUserId() !== lastUserId) onUserChanged();
  }

  /**
   * Reakcja na zmianę `authStore.currentUser` (logowanie, rejestracja, zamówienie
   * gościa, wylogowanie). Przed załadowaniem sm.js to po prostu kolejna próba
   * bootstrapu; po załadowaniu — pytanie o kontakt od razu (z pominięciem memo) i
   * ponowienie po {@link IDENTITY_FOLLOW_UP_MS}.
   */
  function onUserChanged(): void {
    if (!enabled || typeof window === "undefined") return;
    if (!scriptAttached) {
      void bootstrap();
      return;
    }
    const userId = currentUserId();
    if (userId === lastUserId) return;
    lastUserId = userId;
    clearFollowUp();
    if (userId && marketingAllowed() && !identityKnown(userId)) void syncIdentity(userId);
  }

  /**
   * Cofnięcie zgody marketingowej (true → false). Kasuje ciasteczka SM, rekord
   * tożsamości i memo. Jeśli sm.js już działa, przeładowuje stronę — skrypt podpiął
   * się pod pushState i śledziłby dalej nawigację SPA, a po przeładowaniu (bez zgody)
   * się nie załaduje. Bez załadowanego skryptu wystarczy wyczyścić stan.
   */
  function onConsentRevoked(): void {
    if (!enabled || typeof window === "undefined") return;
    clearStoredIdentity(SM_COOKIES);
    if (scriptAttached) window.location.reload();
  }

  /**
   * Po usunięciu konta. Backend kasuje mapowanie klienta na kontakt i zleca
   * usunięcie kontaktu w SM, a `smclient` w przeglądarce dalej przypisywałby do
   * niego wizyty — i anonimowe koszyki, bo to ciasteczko czyta też backend (trafia
   * do niego z nagłówkiem cookie). Kasujemy też identyfikator urządzenia (`smuuid`):
   * SM wiąże go z kontaktem, a sm.js przy pustym `smclient` zostawia stary `smuuid`
   * (sam, przy czyszczeniu kontaktu, kasuje oba). Zgoda marketingowa trwa, więc sm.js
   * wygeneruje nowy identyfikator urządzenia.
   *
   * Bez bramki `enabled`: ciasteczko mogło zostać z czasu, gdy integracja była
   * włączona, a backend czyta je niezależnie od frontu.
   *
   * Zwraca `true`, gdy sm.js już działa: trzyma on kontakt w pamięci do pełnego
   * wejścia na stronę, więc dalsza nawigacja SPA w tej karcie szłaby na usunięty
   * kontakt — wołający musi przejść dalej pełnym przeładowaniem. Sama strona się
   * tu nie przeładowuje, bo o adresie docelowym decyduje wołający.
   */
  function onAccountDeleted(): boolean {
    if (typeof window === "undefined") return false;
    clearStoredIdentity(CONTACT_IDENTITY_COOKIES);
    // Przed załadowaniem sm.js tożsamość czeka jeszcze w zmiennej globalnej.
    delete window._smclientid;
    return scriptAttached;
  }

  return { enabled, bootstrap, onUserChanged, onConsentRevoked, onAccountDeleted };
}

// Tylko do testów — sm.js jest jeden na dokument, a testy potrzebują czystego stanu.
export function __resetSalesmanagoForTests(): void {
  clearFollowUp();
  scriptAttached = false;
  bootstrapping = false;
  lastUserId = null;
}
